import { useState } from 'react';
import {
  Clock,
  Download,
  FileCheck,
  FlaskConical,
  MapPin,
  Package,
  Shield,
  Truck,
} from 'lucide-react';
import { ACTIVE_CONSIGNMENTS, CONSIGNMENT_STATUS } from './mockCustomerPortalData';

const P = {
  silver: '#F1F5F9',
  white: '#FFFFFF',
  grid: '#E2E8F0',
  blue: '#3B82F6',
  btnBlue: '#BFDBFE',
  btnBlueBorder: '#93C5FD',
};

function DocButton({ label, icon: Icon, available, onDownload }) {
  return (
    <button
      type="button"
      disabled={!available}
      onClick={onDownload}
      className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition disabled:cursor-not-allowed disabled:opacity-50"
      style={
        available
          ? {
              backgroundColor: P.btnBlue,
              borderColor: P.btnBlueBorder,
              color: '#000000',
              borderWidth: 1,
              borderStyle: 'solid',
            }
          : {
              backgroundColor: P.silver,
              borderColor: P.grid,
              color: 'rgba(0,0,0,0.45)',
              borderWidth: 1,
              borderStyle: 'solid',
            }
      }
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      {available && <Download className="h-3 w-3 opacity-60" />}
    </button>
  );
}

export default function DeliveryTracker({ consignments = ACTIVE_CONSIGNMENTS }) {
  const [toast, setToast] = useState('');

  const handleDownload = (docType, conId) => {
    setToast(`${docType} for ${conId} — download queued (UI preview).`);
    setTimeout(() => setToast(''), 2500);
  };

  return (
    <section
      className="overflow-hidden rounded-2xl border shadow-sm"
      style={{ backgroundColor: P.white, borderColor: P.grid, borderWidth: 1, borderStyle: 'solid' }}
    >
      <div
        className="border-b px-5 py-4"
        style={{ backgroundColor: P.silver, borderColor: P.grid, borderBottomWidth: 1, borderBottomStyle: 'solid' }}
      >
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5" style={{ color: P.blue }} strokeWidth={1.75} />
          <div>
            <h3 className="text-base font-bold text-black">Client Live Delivery Tracker</h3>
            <p className="text-xs text-black/70">{consignments.length} active consignments</p>
          </div>
        </div>
      </div>

      {toast && (
        <div
          className="border-b px-5 py-2 text-xs font-semibold text-black"
          style={{ backgroundColor: '#DCFCE7', borderColor: '#86EFAC' }}
        >
          {toast}
        </div>
      )}

      <div className="divide-y" style={{ borderColor: P.grid }}>
        {consignments.map((c) => {
          const status = CONSIGNMENT_STATUS[c.status];
          return (
            <div
              key={c.id}
              className="p-5 transition hover:bg-[#F8FAFC]"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${status.className}`}
                    >
                      {status.label}
                    </span>
                    <span className="font-mono text-[11px] text-black/60">{c.id}</span>
                  </div>
                  <p className="mt-2 flex items-center gap-2 text-base font-bold text-black">
                    <Package className="h-4 w-4" style={{ color: P.blue }} />
                    {c.product}
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-black/80">{c.volume}</p>
                </div>
                <div className="text-right">
                  <p className="flex items-center justify-end gap-1 text-xs font-semibold text-black/70">
                    <Clock className="h-3.5 w-3.5" style={{ color: P.blue }} />
                    {c.eta}
                  </p>
                  <p className="mt-1 font-mono text-[11px] text-black/60">{c.vehicleNumber}</p>
                </div>
              </div>

              <div className="mt-3 flex items-start gap-1.5 text-xs text-black/70">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-black/50" />
                <span>
                  {c.origin} → {c.destination}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <DocButton
                  label="POD"
                  icon={FileCheck}
                  available={c.documents.pod}
                  onDownload={() => handleDownload('POD', c.id)}
                />
                <DocButton
                  label="COA"
                  icon={FlaskConical}
                  available={c.documents.coa}
                  onDownload={() => handleDownload('COA', c.id)}
                />
                <DocButton
                  label="MSDS"
                  icon={Shield}
                  available={c.documents.msds}
                  onDownload={() => handleDownload('MSDS', c.id)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
